import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { UserPlus, Trash2, ShieldCheck, AlertTriangle, X, Crown, KeyRound, UserCog } from "lucide-react";

const ROLE_BADGE = {
  super_admin: { label: "Super Admin", cls: "bg-[#0A1128] text-[#fda500] border border-[#0A1128]",       Icon: Crown },
  admin:       { label: "Admin",       cls: "bg-[#254391] text-white border border-[#254391]",            Icon: ShieldCheck },
  moderator:   { label: "Moderator",   cls: "bg-slate-600 text-white border border-slate-700",            Icon: UserCog },
  staff:       { label: "Staff",       cls: "bg-slate-100 text-slate-700 border border-slate-200",        Icon: null },
};

export default function AdminUsers() {
  const { user: actor } = useAuth();
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [pwModal, setPwModal] = useState(null);  // user object or null
  const [roleModal, setRoleModal] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const isSuper = actor?.role === "super_admin";
  const isAdmin = isSuper || actor?.role === "admin";

  const allowedNewRoles = (() => {
    if (actor?.role === "super_admin") return ["admin", "moderator", "staff"];
    if (actor?.role === "admin") return ["moderator", "staff"];
    if (actor?.role === "moderator") return ["staff"];
    return [];
  })();

  const load = async () => {
    try { const r = await api.get("/users"); setList(r.data || []); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/users", form);
      setShow(false); setForm({ email: "", name: "", password: "", role: allowedNewRoles[0] || "staff" });
      setMsg("User created."); setTimeout(() => setMsg(""), 2500);
      await load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?`)) return;
    try { await api.delete(`/users/${u.id}`); await load(); }
    catch (e) { alert(formatErr(e.response?.data?.detail)); }
  };

  const canManage = (target) => {
    if (target.id === actor.id) return false;
    if (target.role === "super_admin") return false;
    if (actor.role === "super_admin") return true;
    if (actor.role === "admin") return target.role === "moderator" || target.role === "staff";
    if (actor.role === "moderator") return target.role === "staff";
    return false;
  };

  const canChangePassword = (target) => {
    if (!isAdmin) return false;
    if (target.role === "super_admin") return isSuper;
    if (target.role === "admin") return isSuper || target.id === actor.id;
    return true;
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">Admin · User Management</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A1128]">Users</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isSuper && "Primary Super Admin · full authority"}
            {!isSuper && isAdmin && "Administrator · manage moderators & staff"}
            {actor?.role === "moderator" && "Moderator · manage staff users"}
          </p>
        </div>
        {allowedNewRoles.length > 0 && (
          <button onClick={() => { setForm({ email: "", name: "", password: "", role: allowedNewRoles[0] }); setShow(true); }} className="bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold px-4 py-2 rounded-md flex items-center gap-2 shadow-md" data-testid="add-user-btn">
            <UserPlus size={14} /> Add User
          </button>
        )}
      </div>

      {msg && <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md">{msg}</div>}
      {err && !show && <div className="text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md flex items-center gap-2"><AlertTriangle size={13} />{err}</div>}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-gray-600 border-b border-gray-200">
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Role</th>
              <th className="px-5 py-3 font-semibold">Created</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => {
              const meta = ROLE_BADGE[u.role] || ROLE_BADGE.staff;
              const Ic = meta.Icon;
              return (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-blue-50/30" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-3 font-medium">{u.name}{u.id === actor?.id && <span className="ml-2 text-[10px] uppercase text-gray-400">(you)</span>}</td>
                  <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${meta.cls}`}>
                      {Ic && <Ic size={10} />} {meta.label}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs">{(u.created_at || "").slice(0,10)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {canChangePassword(u) && (
                        <button onClick={() => setPwModal(u)} title="Change password" className="text-[#254391] hover:bg-blue-50 p-1.5 rounded" data-testid={`change-pw-${u.id}`}>
                          <KeyRound size={14} />
                        </button>
                      )}
                      {isSuper && u.role !== "super_admin" && u.id !== actor.id && (
                        <button onClick={() => setRoleModal(u)} title="Change role" className="text-[#254391] hover:bg-blue-50 p-1.5 rounded" data-testid={`change-role-${u.id}`}>
                          <UserCog size={14} />
                        </button>
                      )}
                      {canManage(u) && (
                        <button onClick={() => del(u)} title="Delete user" className="text-red-500 hover:bg-red-50 p-1.5 rounded" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {show && (
        <Modal title="Create User" onClose={() => setShow(false)}>
          <form onSubmit={create} className="space-y-3" data-testid="user-form">
            <Lab label="Name"><input className="inp" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="form-name" /></Lab>
            <Lab label="Email"><input type="email" className="inp" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} required data-testid="form-email" /></Lab>
            <Lab label="Password"><input type="password" className="inp" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} required minLength={6} data-testid="form-password" /></Lab>
            <Lab label="Role">
              <select className="inp" value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} data-testid="form-role">
                {allowedNewRoles.map((r) => <option key={r} value={r}>{ROLE_BADGE[r].label}</option>)}
              </select>
            </Lab>
            {err && <div className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md"><AlertTriangle size={13} />{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={busy} className="px-4 py-1.5 text-sm bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold rounded-md shadow-md" data-testid="form-submit">{busy ? "Creating…" : "Create"}</button>
            </div>
          </form>
        </Modal>
      )}

      {pwModal && (
        <PasswordChangeModal user={pwModal} onClose={() => setPwModal(null)} onSuccess={() => { setMsg("Password updated."); setTimeout(() => setMsg(""), 2500); }} />
      )}

      {roleModal && (
        <RoleChangeModal user={roleModal} onClose={() => setRoleModal(null)} onSuccess={async () => { setMsg("Role updated."); setTimeout(() => setMsg(""), 2500); await load(); }} />
      )}

      <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border:1px solid #d1d5db;border-radius:0.375rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#254391;box-shadow:0 0 0 2px rgba(37,67,145,0.2)}`}</style>
    </div>
  );
}

function PasswordChangeModal({ user, onClose, onSuccess }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/password`, { new_password: pw });
      onSuccess?.(); onClose();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Change password — ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
        <p className="text-xs text-gray-500">Email: <span className="font-mono">{user.email}</span></p>
        <Lab label="New Password"><input type="password" className="inp" value={pw} onChange={(e)=>setPw(e.target.value)} required minLength={6} data-testid="new-password-input" /></Lab>
        <Lab label="Confirm Password"><input type="password" className="inp" value={pw2} onChange={(e)=>setPw2(e.target.value)} required minLength={6} data-testid="confirm-password-input" /></Lab>
        {err && <div className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md"><AlertTriangle size={13} />{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-1.5 text-sm bg-[#254391] hover:bg-[#1E3675] text-white font-bold rounded-md" data-testid="change-password-submit">{busy ? "Saving…" : "Change Password"}</button>
        </div>
      </form>
      <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border:1px solid #d1d5db;border-radius:0.375rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#254391;box-shadow:0 0 0 2px rgba(37,67,145,0.2)}`}</style>
    </Modal>
  );
}

function RoleChangeModal({ user, onClose, onSuccess }) {
  const [role, setRole] = useState(user.role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post(`/users/${user.id}/role`, { role });
      onSuccess?.(); onClose();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Change role — ${user.name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="change-role-form">
        <p className="text-xs text-gray-500">Email: <span className="font-mono">{user.email}</span></p>
        <Lab label="Role">
          <select className="inp" value={role} onChange={(e)=>setRole(e.target.value)} data-testid="role-select">
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
            <option value="staff">Staff</option>
          </select>
        </Lab>
        <div className="text-[11px] text-gray-500 italic">Note: Super Admin role is reserved for the Primary Super Admin and cannot be assigned here.</div>
        {err && <div className="flex items-center gap-2 text-xs bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md"><AlertTriangle size={13} />{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-1.5 text-sm bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold rounded-md" data-testid="change-role-submit">{busy ? "Saving…" : "Change Role"}</button>
        </div>
      </form>
      <style>{`.inp{width:100%;padding:0.5rem 0.75rem;border:1px solid #d1d5db;border-radius:0.375rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#254391;box-shadow:0 0 0 2px rgba(37,67,145,0.2)}`}</style>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-bold uppercase tracking-[0.14em] text-[#254391]">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Lab({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}
